import React, { useState } from 'react';
import { 
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Tooltip, useMediaQuery, useTheme, Button
} from '@mui/material';
import { 
    MdError, MdCalendarToday, MdAccessTime, MdPerson, MdSportsTennis, MdAdd
} from "react-icons/md";
import axios from 'axios';
import dayjs from 'dayjs';
import ShowBookingModal from '../../components/ShowBookingModal';
import AdminBookingModal from './components/AdminBookingModal';
import { API_BASE_URL } from '../../App.js';
import useFetchData from '../../hooks/useFetchData.jsx';

const Bookings = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [bookings, setBookings] = useState([]);
    const [players, setPlayers] = useState([]);
    const [familyMember, setFamilyMember] = useState([]);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [showBooking, setShowBooking] = useState(false);
    const [showAdminBooking, setShowAdminBooking] = useState(false);

    const refreshBookings = () => {
        axios.get(`${API_BASE_URL}/api/allBookings`)
            .then((response) => {
                const now = dayjs();
                const futureBookings = response.data
                    .filter(booking => dayjs(booking.startTime).isAfter(now) && booking.gameType !== 'event')
                    .sort((a, b) => dayjs(a.startTime).unix() - dayjs(b.startTime).unix());
                setBookings(futureBookings);
            })
            .catch((error) => {
                console.error("Fehler beim Abrufen der Buchungen: ", error);
            });
    };

    useFetchData({
        endpoint: '/api/allBookings',
        setter: setBookings,
        transformFn: data => data
            .filter(b => dayjs(b.startTime).isAfter(dayjs()) && b.gameType !== 'event')
            .sort((a, b) => dayjs(a.startTime).unix() - dayjs(b.startTime).unix())
    });

    useFetchData({
        endpoint: '/api/players',
        setter: setPlayers
    });

    useFetchData({
        endpoint: '/api/allfamilymember',
        setter: setFamilyMember
    });

    const getPlayerName = (playerId, snapshot) => {
        const player = players.find(p => p.id === playerId);
        return player ? `${player.name}` : (snapshot || 'Spieler gelöscht');
    };

    const getPartnerName = (partnerIds, partnerSnapshots = []) => {
        const firstPartnerId = partnerIds[0];
        const partner = (players.find(p => p.id === firstPartnerId) || familyMember.find(p => p._id === firstPartnerId));
        return partner ? `${partner.name}` : (partnerSnapshots[0] || 'Unbekannter Partner');
    };

    const handleClick = (booking) => {
        setSelectedBooking(booking);
        setShowBooking(true);
    };

    const handleClose = (value) => {
        setShowBooking(false);
        if (value) {
            refreshBookings();
        }
    };

    const handleAdminBookingClose = (success) => {
        setShowAdminBooking(false);
        if (success === true) {
            refreshBookings();
        }
    };

    return (
        <Box>
            <Paper sx={{ p: { xs: 2, md: 3 } }} key="bookings-paper">
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 3,
                    borderBottom: '1px solid rgba(0,0,0,0.1)',
                    pb: 2
                }}>
                    <Typography variant="h5" component="h1" color="primary" sx={{ fontWeight: 'bold' }}>
                        Kommende Buchungen
                    </Typography>
                    <Button 
                        variant="contained" 
                        color="primary"
                        startIcon={<MdAdd />}
                        onClick={() => setShowAdminBooking(true)}
                        sx={{ 
                            borderRadius: '20px',
                            px: 3,
                            boxShadow: '0 4px 10px rgba(211, 47, 47, 0.3)'
                        }}
                    >
                        Neue Buchung
                    </Button>
                </Box>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <MdCalendarToday size={18} /> Datum
                                    </Box>
                                </TableCell>
                                {!isMobile && (
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <MdAccessTime size={18} /> Zeit
                                        </Box>
                                    </TableCell>
                                )}
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <MdPerson size={18} /> Bucher
                                    </Box>
                                </TableCell>
                                {!isMobile && (
                                    <TableCell sx={{ fontWeight: 'bold' }}>Partner</TableCell>
                                )}
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                        <MdSportsTennis size={18} /> Platz
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {bookings.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={isMobile ? 3 : 5} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">Keine zukünftigen Buchungen gefunden.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                bookings.map((booking) => (
                                    <TableRow 
                                        key={booking._id} 
                                        hover 
                                        onClick={() => handleClick(booking)}
                                        sx={{ 
                                            cursor: 'pointer',
                                            bgcolor: booking.status === 1 ? 'rgba(211, 47, 47, 0.04)' : 'inherit',
                                            opacity: booking.status === 1 ? 0.7 : 1
                                        }}
                                    >
                                        <TableCell sx={{ fontSize: '0.85rem' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {booking.status === 1 && (
                                                    <Tooltip title={`Storniert wegen: ${booking.cancelledByEventId?.name || 'Unbekannt'}`}>
                                                        <Box component="span" sx={{ display: 'flex' }}>
                                                            <MdError color={theme.palette.error.main} size={18} />
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                                {dayjs(booking.startTime).format("DD.MM.YY")}
                                            </Box>
                                            {isMobile && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    {dayjs(booking.startTime).format("HH:mm")} - {dayjs(booking.endTime).format("HH:mm")}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        {!isMobile && (
                                            <TableCell sx={{ fontSize: '0.85rem' }}>
                                                {dayjs(booking.startTime).format("HH:mm")} - {dayjs(booking.endTime).format("HH:mm")}
                                            </TableCell>
                                        )}
                                        <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                            {getPlayerName(booking.playerId, booking.playerSnapshot)}
                                            {isMobile && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                    {getPartnerName(booking.partners, booking.partnersSnapshot)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        {!isMobile && (
                                            <TableCell sx={{ fontSize: '0.85rem' }}>
                                                {getPartnerName(booking.partners, booking.partnersSnapshot)}
                                            </TableCell>
                                        )}
                                        <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            {booking.courtId}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <ShowBookingModal 
                show={showBooking} 
                onHide={handleClose} 
                booking={selectedBooking} 
                players={players} 
            />

            <AdminBookingModal
                open={showAdminBooking}
                onClose={handleAdminBookingClose}
                players={players}
            />
        </Box>
    );
};

export default Bookings;
